import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Mail, Clock, Users, UserRound, CalendarDays } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { PageHeader } from '../components/SectionHeader';
import { NEWS, TRAINING, EVENTS, findById } from '../data/demo';
import { NewsCard, TrainingCard, EventCard } from '../components/Cards';
import { CategoryTag, DemoBadge } from '../components/Badges';
import { Pagination, usePagination } from '../components/Pagination';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/ui/button';
import { useI18n } from '../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Checks training enrol links against the backend; dead offers (404/410/unreachable) are removed from the list.
const useLiveTraining = () => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    const urls = TRAINING.map((t) => t.enrollUrl).filter(Boolean);
    fetch(`${API}/links/check`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ urls }) })
      .then((r) => (r.ok ? r.json() : {}))
      .then(setStatus)
      .catch(() => setStatus({}));
  }, []);
  const list = useMemo(() => TRAINING.filter((t) => !status || !t.enrollUrl || status[t.enrollUrl]?.alive !== false), [status]);
  return { list, status, checking: status === null, removed: status ? TRAINING.length - list.length : 0 };
};

const listingConfig = {
  news: { data: NEWS, Card: NewsCard, titleKey: 'nav.actualidad', subKey: 'section.news.sub' },
  training: { data: TRAINING, Card: TrainingCard, titleKey: 'nav.formacion', subKey: 'section.training.sub' },
  events: { data: EVENTS, Card: EventCard, titleKey: 'nav.eventos', subKey: 'section.events.sub' },
};

export const ContentListingPage = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const type = ['news', 'training', 'events'].includes(params.get('tipo')) ? params.get('tipo') : 'all';
  const query = params.get('q') || '';
  const live = useLiveTraining();
  const all = useMemo(() => [
    ...NEWS.map((item) => ({ ...item, kind: 'news' })),
    ...(user ? live.list.map((item) => ({ ...item, kind: 'training' })) : []),
    ...EVENTS.map((item) => ({ ...item, kind: 'events' })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date)), [live.list, user]);
  const items = all.filter((item) => (type === 'all' || item.kind === type) && `${item.title} ${item.summary} ${item.category}`.toLowerCase().includes(query.toLowerCase()));
  const pagination = usePagination(items, 9, `${type}|${query}|${!!user}`);
  const setFilter = (key, value) => { const next = new URLSearchParams(params); if (value && value !== 'all') next.set(key, value); else next.delete(key); setParams(next, { replace: key === 'q' }); };
  const locked = type === 'training' && !user;
  return <div className="min-h-screen bg-[#0B0C0E]"><Navbar />
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="content-hub">
      <PageHeader title={t('content.hubTitle')} subtitle={t('content.hubSubtitle')} testId="content-hub-header" />
      <div className="border-y border-[#2A2F3D] py-4 mb-8 flex flex-col md:flex-row gap-4 md:items-center justify-between">
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('content.filter')} data-testid="content-hub-filters">{[['all', 'content.all'], ['news', 'content.news'], ['training', 'content.training'], ['events', 'content.events']].map(([id, key]) => <button key={id} data-testid={`content-group-tab-${id}`} aria-pressed={type === id} onClick={() => setFilter('tipo', id)} className={`px-4 py-2 text-xs font-mono rounded-full border transition-colors ${type === id ? 'text-slate-950 bg-amber-500 border-amber-500' : 'text-slate-300 border-[#2A2F3D] hover:border-amber-500/50'}`}>{t(key)}</button>)}</div>
        <input data-testid="content-hub-search" value={query} onChange={(e) => setFilter('q', e.target.value)} aria-label={t('content.search')} placeholder={t('content.search')} className="bg-[#121418] border border-[#2A2F3D] rounded-lg py-2.5 px-4 text-sm focus:border-amber-500 outline-none min-w-0 md:w-72" />
      </div>
      {!user && (type === 'all' || locked) && <div data-testid="content-training-gate" className="sc-card p-6 mb-8 flex flex-col sm:flex-row gap-5 sm:items-center justify-between"><p className="text-sm text-slate-400 max-w-xl">{t('gate.subtitle')}</p><Button asChild data-testid="content-register"><Link to="/registro">{t('gate.register')}</Link></Button></div>}
      {!locked && <><p className="text-xs font-mono text-slate-500 mb-5" data-testid="content-results-count">{items.length} {t('meta.results')} · {t('content.readOnly')}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-8" data-testid="content-hub-grid">{pagination.items.map((item) => { const Card = listingConfig[item.kind].Card; return <div key={`${item.kind}-${item.id}`} className="flex flex-col min-w-0" data-testid={`content-item-${item.kind}-${item.id}`}><span className={`text-[10px] font-mono uppercase tracking-widest mb-2 ${item.kind === 'training' ? 'text-emerald-400' : item.kind === 'events' ? 'text-sky-400' : 'text-amber-500'}`}>{t(`content.${item.kind}`)}</span><Card item={item} /></div>; })}</div>
        {!items.length && <p data-testid="content-hub-empty" className="text-sm text-slate-400 py-12">{t('empty.subtitle')}</p>}
        <Pagination {...pagination} testId="content-hub" />
      </>}
    </main><Footer /></div>;
};

const detailConfig = {
  news: { data: NEWS, back: '/actualidad', label: 'Actualidad y agenda' },
  training: { data: TRAINING, back: '/actualidad?tipo=training', label: 'Actualidad y agenda · Cursos' },
  events: { data: EVENTS, back: '/actualidad?tipo=events', label: 'Actualidad y agenda · Eventos' },
};

export const ContentDetailPage = ({ kind }) => {
  const { locale } = useI18n();
  const { id } = useParams();
  const { data, back, label } = detailConfig[kind];
  const item = findById(data, id);
  if (!item) return (
    <div className="min-h-screen bg-[#0B0C0E]"><Navbar />
      <div className="max-w-4xl mx-auto px-4 py-24 text-center">
        <h1 className="text-3xl font-bold text-white mb-4">No encontrado</h1>
        <Link to={back} className="text-amber-500 font-mono uppercase tracking-wider text-sm">← {label}</Link>
      </div><Footer /></div>
  );
  const fmt = (iso) => new Date(iso).toLocaleDateString(locale === 'ca' ? 'ca-ES' : locale === 'en' ? 'en-GB' : 'es-ES', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <Link to={back} className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-slate-400 hover:text-amber-500 mb-8">
          <ArrowLeft size={14} /> {label}
        </Link>
        <div className="flex flex-wrap gap-2 mb-5">
          <CategoryTag name={item.category} accent="#F59E0B" />
          <DemoBadge />
        </div>
        <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h1>
        <div className="text-sm text-slate-500 font-mono uppercase tracking-wider mb-8">
          {kind === 'news' && `${fmt(item.date)} · ${item.source}`}
          {kind === 'training' && `${item.school} · ${item.modality} · ${fmt(item.date)}`}
          {kind === 'events' && `${item.location} · ${fmt(item.date)}`}
        </div>
        {item.image && (
          <div className="rounded-xl overflow-hidden border border-[#2A2F3D] mb-8 aspect-[16/9]">
            <img src={item.image} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
          </div>
        )}
        <div className="sc-card p-8" data-testid="content-summary-card">
          <div className="sc-mono-eyebrow text-amber-500 mb-3">Resumen</div>
          <p className="text-slate-300 leading-relaxed text-base md:text-lg">{item.summary}</p>

          {kind === 'news' && item.body?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[#2A2F3D] space-y-4" data-testid="news-body">
              <div className="sc-mono-eyebrow text-slate-500">Artículo</div>
              {item.body.map((p, i) => <p key={i} className="text-slate-300 leading-relaxed">{p}</p>)}
            </div>
          )}

          {kind === 'news' && item.sourceUrl && (
            <div className="mt-6 pt-6 border-t border-[#2A2F3D]">
              <div className="sc-mono-eyebrow text-slate-500 mb-3">Artículo completo</div>
              <a
                href={item.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="news-source-link"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold px-5 py-3 rounded-lg transition-colors"
              >
                <ExternalLink size={14} />
                Leer el artículo completo en {item.source}
              </a>
              <div className="text-[11px] text-slate-500 font-mono mt-3">Se abrirá en la web original en una pestaña nueva.</div>
            </div>
          )}

          {kind === 'training' && (
            <div className="mt-6 pt-6 border-t border-[#2A2F3D] space-y-6" data-testid="training-details">
              {item.description && <div><div className="sc-mono-eyebrow text-slate-500 mb-2">Descripción completa</div><p className="text-slate-300 leading-relaxed text-sm md:text-base" data-testid="training-description">{item.description}</p></div>}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Fact icon={Clock} label="Duración" value={item.duration} />
                <Fact icon={CalendarDays} label="Horario" value={item.schedule} />
                <Fact icon={Users} label="Plazas" value={item.places} />
                <Fact icon={UserRound} label="Formador/a" value={item.instructor} />
              </div>
              {item.program?.length > 0 && (
                <div><div className="sc-mono-eyebrow text-slate-500 mb-2">Programa</div>
                  <ul className="space-y-1.5">{item.program.map((p) => <li key={p} className="text-sm text-slate-300 flex gap-2"><span className="w-1 h-1 rounded-full bg-amber-500 mt-2 shrink-0" />{p}</li>)}</ul></div>
              )}
              {item.requirements?.length > 0 && (
                <div><div className="sc-mono-eyebrow text-slate-500 mb-2">Requisitos</div>
                  <ul className="space-y-1.5">{item.requirements.map((p) => <li key={p} className="text-sm text-slate-300 flex gap-2"><span className="w-1 h-1 rounded-full bg-slate-500 mt-2 shrink-0" />{p}</li>)}</ul></div>
              )}
              <div className="flex items-center justify-between flex-wrap gap-3 pt-4 border-t border-[#2A2F3D]">
                <span className="sc-mono-eyebrow text-slate-500">Precio</span>
                <span className="font-mono text-amber-500 text-xl font-semibold">{item.price}</span>
              </div>
              <div className="sc-mono-eyebrow text-slate-500">Inscripción en la web oficial</div>
              <div className="flex flex-col sm:flex-row gap-3">
                {item.enrollUrl && (
                  <a
                    href={item.enrollUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="training-enroll-link"
                    className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold px-5 py-3 rounded-lg transition-colors"
                  >
                    <ExternalLink size={14} />
                    Inscribirme al curso
                  </a>
                )}
                {item.contactEmail && (
                  <a
                    href={`mailto:${item.contactEmail}`}
                    data-testid="training-contact-link"
                    className="inline-flex items-center justify-center gap-2 border border-[#2A2F3D] hover:border-amber-500/40 text-slate-200 font-medium px-5 py-3 rounded-lg transition-colors"
                  >
                    <Mail size={14} />
                    Contactar con la escuela
                  </a>
                )}
              </div>
              {item.verifiedAt && <p className="text-[11px] text-slate-500 font-mono" data-testid="training-verified">Enlace comprobado el {fmt(item.verifiedAt)} · se vuelve a comprobar automáticamente en cada visita al listado.</p>}
            </div>
          )}
        </div>
      </article>
      <Footer />
    </div>
  );
};

const Fact = ({ icon: Icon, label, value }) => (
  <div className="sc-card bg-[#0B0C0E] p-3">
    <div className="flex items-center gap-1.5 text-slate-500 mb-1"><Icon size={12} /><span className="text-[10px] font-mono uppercase tracking-widest">{label}</span></div>
    <div className="text-sm text-slate-200">{value || '—'}</div>
  </div>
);
